import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MonacoEditor from "@monaco-editor/react";
import {
  FaJava,
  FaPython,
  FaCuttlefish,
  FaCode,
  FaJsSquare,
  FaHtml5,
  FaCss3Alt,
  FaReact
} from "react-icons/fa";
import "./CodeInterpreter.css";
import { runJavaProgram } from "../Services/courseService";
import { Helmet } from "react-helmet";

const languages = [
  { id: "java", icon: <FaJava />, name: "Java", color: "#f89820" },
  { id: "python", icon: <FaPython />, name: "Python", color: "#306998" },
  { id: "cpp", icon: <FaCuttlefish />, name: "C++", color: "#00599C" },
  { id: "c", icon: <FaCode />, name: "C", color: "#A8B9CC" },
  { id: "javascript", icon: <FaJsSquare />, name: "JavaScript", color: "#f7df1e" },
  { id: "html", icon: <FaHtml5 />, name: "HTML", color: "#e34c26" },
  { id: "css", icon: <FaCss3Alt />, name: "CSS", color: "#264de4" },
  { id: "react", icon: <FaReact />, name: "React", color: "#61dafb" }
];

const defaultCode = {
  java: `// Welcome to Java! 🚀
public class Main {
  public static void main(String[] args) {
    System.out.println("Hello from SKILLZVERSE!");
  }
}`,
  python: `# Welcome to Python! 🐍
print("Hello from SKILLZVERSE!")`,
  cpp: `// Welcome to C++! ⚡
#include <iostream>
using namespace std;
int main() {
  cout << "Hello from SKILLZVERSE!" << endl;
  return 0;
}`,
  c: `// Welcome to C! 🔥
#include <stdio.h>
int main() {
  printf("Hello from SKILLZVERSE!\\n");
  return 0;
}`,
  javascript: `// Welcome to JavaScript! 🌍
console.log("Hello from SKILLZVERSE!");`,
  html: `<!-- Welcome to HTML! 🌐 -->
<!DOCTYPE html>
<html>
<head>
  <title>Live Preview</title>
  <style></style>
</head>
<body>
  <h1>Hello from SKILLZVERSE!</h1>
  <script></script>
</body>
</html>`,
  css: `/* Welcome to CSS! 🎨 */
body {
  background-color: lightblue;
}`,
  react: `// Welcome to React! ⚛
const App = () => {
  const [name, setName] = React.useState("");
  const [age, setAge] = React.useState("");

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <h1>SKILLZVERSE</h1>

      <div style={{ marginBottom: "10px" }}>
        <label>Name: </label>
        <input 
          type="text" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="Enter your name"
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label>Age: </label>
        <input 
          type="number" 
          value={age} 
          onChange={(e) => setAge(e.target.value)} 
          placeholder="Enter your age"
        />
      </div>

      <h2>Welcome, {name || "Guest"}!</h2>
      <h3>Your age is {age}</h3>
    </div>
  );
};
`
};

const CodeInterpreter = () => {
  const [language, setLanguage] = useState(localStorage.getItem("selectedLanguage") || "java");
  const [code, setCode] = useState(defaultCode[language]);
  const [input, setInput] = useState("");

  const [javaOutput, setJavaOutput] = useState("");
  const [pythonOutput, setPythonOutput] = useState("");
  const [cppOutput, setCppOutput] = useState("");
  const [cOutput, setCOutput] = useState("");
  const [javascriptOutput, setJavascriptOutput] = useState("");

  const iframeRef = useRef(null);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    if (storedLanguage) {
      setLanguage(storedLanguage);
      setCode(defaultCode[storedLanguage] || "");
    }
  }, []);

  const handleLanguageChange = (langId) => {
    setLanguage(langId);
    setCode(defaultCode[langId] || "");
    localStorage.setItem("selectedLanguage", langId);
  };

  const handleRun = async () => {
    toast.dismiss();
    if (!code.trim()) {
      toast.error("Code cannot be empty!");
      return;
    }

    if (language === "html" || language === "css" || language === "react") {
      updateIframe();
      return;
    }

    try {
      const requestData = { language, code, input };

      if (language === "java") {
        const response = await runJavaProgram(requestData);
        setJavaOutput(response);
      } else if (language === "python") {
        const response = await runJavaProgram(requestData);
        setPythonOutput(response);
      } else if (language === "cpp") {
        const response = await runJavaProgram(requestData);
        setCppOutput(response);
      } else if (language === "c") {
        const response = await runJavaProgram(requestData);
        setCOutput(response);
      } else if (language === "javascript") {
        const response = await runJavaProgram(requestData);
        setJavascriptOutput(response);
      }

    } catch (error) {
      toast.dismiss();
      toast.error("Error executing code");
    }
  };

  const updateIframe = useCallback(() => {
    if (!iframeRef.current) return;
    let iframeDocument = iframeRef.current.contentDocument;
    iframeDocument.open();

    if (language === "react") {
      iframeDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <script src="https://unpkg.com/react@18/umd/react.development.js"></script>
          <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
          <script src="https://unpkg.com/babel-standalone@6/babel.min.js"></script>
        </head>
        <body>
          <div id="root"></div>
          <script type="text/babel">
            try {
              ${code};
              ReactDOM.createRoot(document.getElementById("root")).render(React.createElement(App));
            } catch (error) {
              document.getElementById("root").innerHTML = "<pre style='color: red;'>Error: " + error.message + "</pre>";
              console.error(error);
            }
          </script>
        </body>
        </html>
      `);
    } else {
      iframeDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>${language === "css" ? code : ""}</style>
        </head>
        <body>
          ${language === "html" ? code : ""}
        </body>
        </html>
      `);
    }

    iframeDocument.close();
  }, [code, language]);

  useEffect(() => {
    if (language === "html" || language === "css" || language === "react") {
      updateIframe();
    }
  }, [code, language, updateIframe]);

  return (
  <>

     <Helmet>
      <title>Coding Challenges Platform | SkillzVerse Arena</title>

      <meta
        name="description"
        content="Explore SkillzVerse Arena for coding challenges, programming competitions, technical skill assessments, hands-on practice, and real-world developer learning."
      />

      <meta
        name="keywords"
        content="Coding Challenges Platform,Coding Challenges,Programming Challenges,Coding Practice Platform,Technical Skill Assessment,Programming Competitions"
      />

      <link
        rel="canonical"
        href="https://skillzverse.com/arena"
      />
    </Helmet>
    <div className="interpreter-container">
      <div className="code-language-container">
        {languages.map((lang) => (
          <div
            key={lang.id}
            className={`code-language-icon ${language === lang.id ? "selected" : ""}`}
            style={{ color: language === lang.id ? lang.color : "#bbb" }}
            onClick={() => handleLanguageChange(lang.id)}
          >
            {lang.icon}
          </div>
        ))}
      </div>

      <div className="code-layout">
        <div className="code-editor">
          <MonacoEditor
            height="600px"
            language={language === "react" ? "javascript" : language}
            theme="vs-light"
            value={code}
            onChange={(newCode) => setCode(newCode)}
            options={{
              fontSize: 14,
              minimap: { enabled: false },
              lineNumbers: "on",
              scrollBeyondLastLine: false,
              wordWrap: "on",
            }}
          />
        </div>

        <div className="side-panel">
          {!(language === "html" || language === "css" || language === "react") && (
            <textarea
              className="code-interpreter-input-box"
              placeholder="Enter input..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          )}

          {!(language === "html" || language === "css" || language === "react") && (
            <div className="output-box">
              <h4>Output:</h4>
              <pre>
                {language === "java" && javaOutput}
                {language === "python" && pythonOutput}
                {language === "cpp" && cppOutput}
                {language === "c" && cOutput}
                {language === "javascript" && javascriptOutput}
              </pre>
            </div>
          )}

          {(language === "html" || language === "css" || language === "react") && (
            <iframe ref={iframeRef} className="output-box live-preview" title="title"></iframe>
          )}
          <button className="run-button" onClick={handleRun}>
            Run {language.charAt(0).toUpperCase() + language.slice(1)}
          </button>
        </div>
      </div>

    </div>
    </>
  );
};

export default CodeInterpreter;
